/**
 * Form field furniture on the brand tokens: the labelled field, its helper line and its
 * inline error ("says how to fix it"). Author: Mahmoud Ragab Ahmed.
 */
import { cn } from "@/lib/utils";

export function Field({ className, ...props }: React.ComponentProps<"div">): React.JSX.Element {
  return (
    <div data-slot="field" className={cn("flex min-w-0 flex-col gap-1.5", className)} {...props} />
  );
}

export function FieldHelper({ className, ...props }: React.ComponentProps<"p">): React.JSX.Element {
  return (
    <p
      data-slot="field-helper"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export function FieldError({
  id,
  message,
}: {
  readonly id: string;
  readonly message: string | null;
}): React.JSX.Element | null {
  if (message === null) {
    return null;
  }
  return (
    <p className="text-sm text-danger" id={id} role="alert" data-testid={id}>
      {message}
    </p>
  );
}

/** A section heading inside a long form. */
export function FormSection({
  className,
  ...props
}: React.ComponentProps<"h4">): React.JSX.Element {
  return (
    <h4
      data-slot="form-section"
      className={cn(
        "mt-2 border-b border-rule pb-1 text-xs tracking-[0.12em] text-muted-foreground uppercase",
        className,
      )}
      {...props}
    />
  );
}

/** A refusal rendered inside its dialog (UX_SPEC 5), in the product's one red. */
export function Refusal({
  message,
  testId = "dialog-refusal",
}: {
  readonly message: string | null;
  readonly testId?: string;
}): React.JSX.Element | null {
  if (message === null) {
    return null;
  }
  return (
    <div
      className="rounded-md border border-danger-edge bg-danger-wash p-3 text-sm text-foreground"
      role="alert"
      data-testid={testId}
    >
      <p className="text-danger">{message}</p>
    </div>
  );
}
